"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Sparkles, ArrowUpRight } from "lucide-react";
import CdnImage from "@/components/CdnImage";
import { useT } from "@/hooks/useT";
import { useMarketData } from "@/hooks/useMarketData";
import { formatUSD, timeAgo } from "../../helpers/format";
import { CARD_BASE } from "../../helpers/styles";
import type { ActivityEvent } from "../../types";

// New Market Card — system event when a new market is created.
// Same slim event-card template; header swaps the actor for a
// Sparkles chip + "New market" label.
export default function NewMarketRow({
  event,
}: {
  event: ActivityEvent;
}) {
  const t = useT();
  const router = useRouter();
  const isKalshi = event.market_slug?.toLowerCase().startsWith('kx');

  const marketTitle = event.market_title;
  const marketHref = event.event_slug
    ? `/event/${event.event_slug}`
    : event.market_slug
      ? `/market/${event.market_slug}`
      : null;

  const { data: liveMarketData } = useMarketData(event.market_slug, {
    enabled: !!event.market_slug,
    source: isKalshi ? 'kalshi' : 'polymarket',
  });
  const marketVolume = liveMarketData?.volume ?? null;
  const marketLiquidity = liveMarketData?.liquidity ?? null;
  const marketEndDate = liveMarketData?.endDate ?? null;
  const marketClosed = liveMarketData?.closed === true;

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[role="button"]')) {
      return;
    }
    router.push(`/activity/${event.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className={CARD_BASE}
    >
      <div className="py-2.5 px-1">
        <div className="flex items-center gap-2.5">
          <span className="flex items-center justify-center w-8 h-8 rounded-[5px] flex-shrink-0 bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-gray-400">
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {t("activity.newMarket.label") || "New market"}
                </span>
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-[4px] flex-shrink-0 ${
                  isKalshi ? 'bg-emerald-500/15' : 'bg-blue-500/15'
                }`}>
                  <img
                    src={isKalshi ? '/logos/kalshi/k-icon.svg' : '/logos/polymarket/icon-white.png'}
                    alt={isKalshi ? 'Kalshi' : 'Polymarket'}
                    className="w-3.5 h-3.5 object-contain"
                  />
                </span>
              </div>
              <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-[5px] bg-gray-100 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.06] text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {timeAgo(event.event_at, t)}
              </span>
            </div>
          </div>
        </div>

        {marketHref && marketTitle && (
          <Link
            href={marketHref}
            prefetch={false}
            onClick={(e) => e.stopPropagation()}
            className="block mt-2.5 p-2.5 rounded-[5px] bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/[0.1] transition-colors"
          >
            <div className="flex items-center gap-2.5">
              {event.market_image && (
                <div className="w-8 h-8 rounded-[5px] overflow-hidden flex-shrink-0 bg-gray-200 dark:bg-white/[0.06]">
                  <CdnImage src={event.market_image} alt={marketTitle} width={32} height={32} sizes="32px" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {marketTitle}
                </h3>
                {(marketVolume != null || marketLiquidity != null || marketEndDate) && (
                  <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-gray-500 dark:text-gray-400 flex-wrap">
                    {marketClosed && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-[5px] bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 font-semibold whitespace-nowrap">
                        {t("activity.badge.marketClosed")}
                      </span>
                    )}
                    {marketVolume != null && marketVolume > 0 && (
                      <span className="whitespace-nowrap tabular-nums">{formatUSD(marketVolume)} vol</span>
                    )}
                    {marketLiquidity != null && marketLiquidity > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap tabular-nums">{formatUSD(marketLiquidity)} liq</span>
                      </>
                    )}
                    {marketEndDate && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="whitespace-nowrap">
                          {new Date(marketEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            </div>
          </Link>
        )}
      </div>
    </motion.div>
  );
}
